import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import { AppDispatch } from "../../../../redux/store";
import {
  clearDeleteSupportResourceState,
  clearNewSupportResourcesState,
  clearUpdateSupportResourceState,
  deleteSupportResourceStateAction,
  getCreateSupportResourceState,
  getDeleteSupportResourceState,
  getSupportResourceState,
  getUpdateSupportResourceState,
  SupportResourceResponse,
  supportResourcesStateAction,
} from "../../../../redux/features/other-slice";
import {
  getErrorState,
  setBreadcrumbState,
} from "../../../../redux/features/common-slice";
import paths from "../../../../navigation/paths";
import PermissionGate from "../../../../core/layouts/permission-gate";
import { UserRole } from "../../../../redux/features/user-slice";
import Table from "../../../../components/table";
import Loading from "../../../../components/loading";
import TableBodyCell from "../../../../components/table-body-cell";
import Button from "../../../../components/button";
import ToastComponent from "../../../../components/toast";
import { adminCat, mgTeam } from "../../../../core/utils/constants";
import EmptyDataOverlay from "../../../../components/empty-data-overlay";
import PropertyActions from "../_components/property-actions";
import NewResourceDialog from "./components/new-resource-dialog";
import UpdateResourceDialog from "./components/update-resource-dialog";

export default function SupportResourcesPage() {
  const navigate = useNavigate();

  const { id, name } = useParams();

  const dispatch = useDispatch<AppDispatch>();
  const resourcesState = useSelector(getSupportResourceState);
  const newResourceState = useSelector(getCreateSupportResourceState);
  const updateResourceState = useSelector(getUpdateSupportResourceState);
  const deleteResourceState = useSelector(getDeleteSupportResourceState);
  const errorState = useSelector(getErrorState);

  const [openNew, setOpenNew] = useState<boolean>(false);
  const [updateResource, setUpdateResource] =
    useState<SupportResourceResponse | null>(null);
  const [isHover, setIsHover] = useState<{
    [id: string]: boolean;
  }>({});

  const fetchResources = useCallback(async () => {
    if (id) {
      await dispatch(supportResourcesStateAction({ categoryId: id as string }));
    }
  }, [dispatch, id]);

  const handleSuccessToast = useCallback(
    (message: string, clearAction: any) => {
      toast(message, { type: "success" });
      setTimeout(() => {
        dispatch(clearAction());
      }, 2000);
    },
    [dispatch]
  );

  const deleteResource = async (id: string) => {
    await dispatch(deleteSupportResourceStateAction({ id: id }));
  };

  // Memoized onMouseEnter handler
  const handleMouseEnter = useCallback((id: string) => {
    setIsHover((prevState) => ({
      ...prevState,
      [id]: true,
    }));
  }, []);

  // Memoized onMouseLeave handler
  const handleMouseLeave = useCallback((id: string) => {
    setIsHover((prevState) => ({
      ...prevState,
      [id]: false,
    }));
  }, []);

  useEffect(() => {
    fetchResources();
    dispatch(
      setBreadcrumbState([
        { id: "summaryProperties", path: null, title: "Summary Properties" },
        { id: paths.SUPPORT_RESOURCES, path: null, title: "Support Resources" },
        { id: name as string, path: null, title: name as string },
      ])
    );
  }, [fetchResources]);

  useEffect(() => {
    if (newResourceState.data && newResourceState.data.success) {
      handleSuccessToast(
        newResourceState.data.message,
        clearNewSupportResourcesState
      );
      fetchResources();
    }
  }, [newResourceState, handleSuccessToast]);

  useEffect(() => {
    if (updateResourceState.data && updateResourceState.data.success) {
      handleSuccessToast(
        updateResourceState.data.message,
        clearUpdateSupportResourceState
      );
    }
  }, [updateResourceState, handleSuccessToast]);

  useEffect(() => {
    if (deleteResourceState.data && deleteResourceState.data.success) {
      handleSuccessToast(
        deleteResourceState.data.message,
        clearDeleteSupportResourceState
      );
    }
  }, [deleteResourceState, handleSuccessToast]);

  useEffect(() => {
    if (errorState) {
      if (errorState.redirect) {
        navigate(errorState.redirect.destination, {
          replace: errorState.redirect.permanent,
        });
        return;
      }
      toast(errorState.message, { type: "error" });
    }
  }, [errorState]);

  return (
    <PermissionGate
      categories={[adminCat.id]}
      roles={[UserRole.Admin, UserRole.Supervisor]}
    >
      <Helmet title="Support Resources" />
      <div className="flex items-center justify-end mb-4">
        <Button
          label="Add Resource"
          onClick={() => setOpenNew(true)}
          exClassName="truncate"
        />
      </div>
      <Table
        style={{
          maxHeight: "calc(100% - 48px)",
        }}
        headerCells={[
          { id: "title", label: "Title", align: "text-left", width: "w-5/12" },
          {
            id: "description",
            label: "Description",
            align: "text-left",
            width: "w-7/12",
          },
          // {
          //   id: "group",
          //   label: "Group",
          //   align: "text-left",
          //   width: "w-2/12",
          // },
        ]}
        body={
          resourcesState.loading
            ? [
                <tr key="loading-row">
                  <td colSpan={2} className="h-64">
                    <div className="flex flex-col items-center justify-center">
                      <Loading />
                    </div>
                  </td>
                </tr>,
              ]
            : resourcesState.data.length > 0
            ? [
                ...resourcesState.data.map(
                  (res: SupportResourceResponse, index: number) => {
                    return (
                      <tr
                        key={res.id}
                        className="border-t border-dashed border-divider hover:bg-action-hover relative"
                        onMouseEnter={() => handleMouseEnter(res.id)}
                        onMouseLeave={() => handleMouseLeave(res.id)}
                      >
                        <TableBodyCell exClassName="font-medium whitespace-normal break-words">
                          <label className="cursor-pointer hover:underline">
                            {res.title}
                          </label>
                        </TableBodyCell>
                        <TableBodyCell exClassName="whitespace-normal break-words text-justify">
                          {res.description}
                        </TableBodyCell>
                        {/* <TableBodyCell exClassName="w-2/12 whitespace-normal break-words text-justify">
                          {res.position?.name || "All"}
                        </TableBodyCell> */}
                        {isHover[res.id] && (
                          <PropertyActions
                            id={res.id}
                            onUpdate={() => setUpdateResource(res)}
                            onDelete={() => deleteResource(res.id)}
                          />
                        )}
                      </tr>
                    );
                  }
                ),
              ]
            : [
                <tr key="empty-row">
                  <td colSpan={2} className="h-64">
                    <div className="flex flex-col items-center justify-center">
                      <EmptyDataOverlay
                        label="No Support Resources found"
                        exClassName="h-20"
                      />
                    </div>
                  </td>
                </tr>,
              ]
        }
      />
      <ToastComponent id="resources" />
      <NewResourceDialog
        categoryId={id as string}
        open={openNew}
        setOpen={setOpenNew}
        onCompleted={fetchResources}
      />
      <UpdateResourceDialog
        resource={updateResource}
        setResource={setUpdateResource}
        onCompleted={fetchResources}
      />
    </PermissionGate>
  );
}
